<template>
  <div class="food-stock-manage">
    <a-config-provider :locale="zhCN">
      <a-card :bordered="false">
        <div class="page-header">
          <button class="page-back-btn" @click="goHome">
            <ArrowLeftOutlined />
          </button>
          <span class="page-title">菜品库存管理</span>
        </div>

        <a-form
          :model="queryParams"
          layout="inline"
          class="search-form"
          @submit="handleSearch"
          @submit.prevent
        >
          <a-form-item label="店铺">
            <a-select
              v-model:value="queryParams.data.shopId"
              placeholder="请选择店铺"
              disabled
              style="width: 200px"
              @change="handleShopChange"
            >
              <a-select-option v-for="shop in shopList" :key="shop.shopId" :value="shop.shopId">
                {{ shop.shopName }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="分类">
            <a-select
              v-model:value="queryParams.data.shopFoodCategoryId"
              placeholder="请选择分类"
              allow-clear
              style="width: 200px"
              :disabled="!queryParams.data.shopId"
            >
              <a-select-option
                v-for="cat in searchCategoryOptions"
                :key="cat.shopFoodCategoryId"
                :value="cat.shopFoodCategoryId"
              >
                {{ cat.categoryName }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="菜品名称">
            <a-input
              v-model:value="queryParams.data.shopFoodName"
              placeholder="请输入菜品名称"
              allow-clear
              style="width: 200px"
            />
          </a-form-item>

          <a-form-item label="状态">
            <a-select
              v-model:value="queryParams.data.shopFoodStatus"
              placeholder="请选择状态"
              allow-clear
              style="width: 120px"
            >
              <a-select-option :value="0">上架</a-select-option>
              <a-select-option :value="1">下架</a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="搜索字段">
            <a-input
              v-model:value="queryParams.data.searchField"
              placeholder="支持菜品名称/描述等模糊搜索"
              allow-clear
              style="width: 260px"
            />
          </a-form-item>

          <a-form-item label="创建时间">
            <a-range-picker
              v-model:value="dateRange"
              format="YYYY-MM-DD"
              style="width: 240px"
              @change="handleDateRangeChange"
            />
          </a-form-item>

          <a-form-item>
            <a-button type="primary" html-type="submit" :loading="loading">
              <template #icon><SearchOutlined /></template>
              查询
            </a-button>
            <a-button style="margin-left: 8px" @click="handleReset">
              <template #icon><ReloadOutlined /></template>
              重置
            </a-button>
            <a-button style="margin-left: 8px" @click="openImportFilePicker">
              <template #icon><UploadOutlined /></template>
              Excel 导入
            </a-button>
            <a-button style="margin-left: 8px" :loading="exporting" @click="handleExport">
              <template #icon><DownloadOutlined /></template>
              导出 Excel
            </a-button>
            <span class="ml-2 text-sm text-gray-500">
              Excel 导入库存须包含“菜品ID”和“库存数量”两列
            </span>
            <input
              ref="importFileInput"
              type="file"
              accept=".xlsx,.xls"
              style="display: none"
              @change="handleImportFileChange"
            />
          </a-form-item>
        </a-form>

        <a-table
          :columns="columns"
          :data-source="tableData"
          :loading="loading"
          :pagination="false"
          row-key="shopFoodId"
          :scroll="{ x: 'max-content' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'shopName'">
              <span>{{ getShopName(record.shopId) }}</span>
            </template>
            <template v-else-if="column.key === 'categoryName'">
              <span>{{ getCategoryName(record.shopFoodCategoryId) }}</span>
            </template>
            <template v-else-if="column.key === 'tags'">
              <a-space v-if="getFoodTagNames(record.tags).length > 0" :size="[0, 4]" wrap>
                <a-tag v-for="tag in getFoodTagNames(record.tags)" :key="tag" color="blue">
                  {{ tag }}
                </a-tag>
              </a-space>
              <span v-else>-</span>
            </template>
            <template v-else-if="column.key === 'stockQuantity'">
              <a-tag :color="getStockColor(record.stockQuantity)">
                {{ record.stockQuantity ?? 0 }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'shopFoodStatus'">
              <a-tag :color="record.shopFoodStatus === 0 ? 'green' : 'red'">
                {{ record.shopFoodStatus === 0 ? '上架' : '下架' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'modifyTime'">
              <span>{{ formatDateTime(record.modifyTime || record.createTime) }}</span>
            </template>
            <template v-else-if="column.key === 'action'">
              <a-button type="link" size="small" @click="openStockModal(record)">
                修改库存
              </a-button>
            </template>
          </template>
        </a-table>

        <div class="pagination-container">
          <a-pagination
            v-model:current="queryParams.pageNum"
            v-model:page-size="queryParams.pageSize"
            :total="total"
            :show-total="(total) => `共 ${total} 条`"
            :show-size-changer="true"
            :page-size-options="['10', '20', '50', '100']"
            @change="handlePageChange"
            @show-size-change="handlePageSizeChange"
          />
        </div>
      </a-card>

      <a-modal
        v-model:open="stockModalVisible"
        title="修改菜品库存"
        :confirm-loading="stockSubmitting"
        ok-text="保存"
        cancel-text="取消"
        @ok="handleStockConfirm"
        @cancel="handleStockCancel"
      >
        <a-descriptions v-if="currentStockFood" :column="1" bordered size="small">
          <a-descriptions-item label="主菜品ID">
            {{ currentStockFood.foodId }}
          </a-descriptions-item>
          <a-descriptions-item label="店铺菜品ID">
            {{ currentStockFood.shopFoodId }}
          </a-descriptions-item>
          <a-descriptions-item label="菜品名称">
            {{ currentStockFood.shopFoodName || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="所属店铺">
            {{ getShopName(currentStockFood.shopId) }}
          </a-descriptions-item>
          <a-descriptions-item label="所属分类">
            {{ getCategoryName(currentStockFood.shopFoodCategoryId) }}
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            {{ currentStockFood.shopFoodStatus === 0 ? '上架' : '下架' }}
          </a-descriptions-item>
        </a-descriptions>

        <a-form layout="vertical" class="stock-form">
          <a-form-item label="库存数量" required>
            <a-input-number
              v-model:value="stockForm.stockQuantity"
              :min="0"
              :precision="0"
              style="width: 100%"
              placeholder="请输入库存数量"
            />
          </a-form-item>
        </a-form>
      </a-modal>

      <a-modal
        v-model:open="importModalVisible"
        title="Excel 库存导入"
        width="720px"
        :confirm-loading="importSubmitting"
        :ok-button-props="{ disabled: importErrors.length > 0 || importRows.length === 0 }"
        ok-text="确认导入"
        cancel-text="取消"
        @ok="handleImportConfirm"
        @cancel="handleImportCancel"
      >
        <a-alert
          class="mb-4"
          type="info"
          show-icon
          message="请使用固定列名“菜品ID”和“库存数量”，其他列将被忽略。"
        />
        <div class="mb-3 text-sm text-gray-600">
          文件：{{ importFileName || '-' }}，有效数据 {{ importRows.length }} 条
        </div>
        <a-alert
          v-if="importErrors.length > 0"
          class="mb-4"
          type="error"
          show-icon
          message="文件校验失败，请修正后重新选择"
        >
          <template #description>
            <ul class="m-0 pl-5 max-h-40 overflow-y-auto">
              <li v-for="error in importErrors" :key="error">{{ error }}</li>
            </ul>
          </template>
        </a-alert>
        <a-table
          v-if="importRows.length > 0"
          :columns="importColumns"
          :data-source="importRows"
          :pagination="{ pageSize: 10, hideOnSinglePage: true }"
          row-key="foodId"
          size="small"
          :scroll="{ y: 320 }"
        />
      </a-modal>
    </a-config-provider>
  </div>
</template>

<script setup lang="ts">
  import { nextTick, onMounted, reactive, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    ArrowLeftOutlined,
    DownloadOutlined,
    ReloadOutlined,
    SearchOutlined,
    UploadOutlined,
  } from '@ant-design/icons-vue';
  import { message } from 'ant-design-vue';
  import zhCN from 'ant-design-vue/es/locale/zh_CN';
  import dayjs, { type Dayjs } from 'dayjs';
  import * as XLSX from 'xlsx';
  import type { FoodStockItemModel } from '@/model/food';
  import { fetchCategoryItemList, fetchCategoryList, initializeFoodStock } from '@/service/food';
  import type { FoodStockImportItemModel } from '@/service/food';
  import { fetchShopList } from '@/service/base';

  interface ShopOption {
    shopId?: number;
    shopName?: string;
  }

  interface CategoryOption {
    shopFoodCategoryId?: number;
    shopId?: number;
    categoryName?: string;
  }

  interface QueryData {
    shopId?: number;
    shopFoodCategoryId?: number;
    shopFoodName: string;
    shopFoodStatus?: number;
    searchField: string;
  }

  interface FoodQueryRequest {
    pageNum: number;
    pageSize: number;
    data: Partial<QueryData>;
    createtimeBegin?: string;
    createtimeEnd?: string;
  }

  interface ImportExcelRow {
    菜品ID?: unknown;
    库存数量?: unknown;
  }

  const columns = [
    {
      title: '菜品ID',
      dataIndex: 'foodId',
      key: 'foodId',
      width: 100,
    },
    {
      title: '店铺菜品ID',
      dataIndex: 'shopFoodId',
      key: 'shopFoodId',
      width: 90,
    },
    {
      title: '菜品名称',
      dataIndex: 'shopFoodName',
      key: 'shopFoodName',
      width: 180,
    },
    {
      title: '所属店铺',
      dataIndex: 'shopId',
      key: 'shopName',
      width: 160,
    },
    {
      title: '所属分类',
      dataIndex: 'shopFoodCategoryId',
      key: 'categoryName',
      width: 160,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 180,
    },
    {
      title: '当前库存',
      dataIndex: 'stockQuantity',
      key: 'stockQuantity',
      width: 110,
      sorter: (a: FoodStockItemModel, b: FoodStockItemModel) =>
        (a.stockQuantity ?? 0) - (b.stockQuantity ?? 0),
    },
    {
      title: '状态',
      dataIndex: 'shopFoodStatus',
      key: 'shopFoodStatus',
      width: 100,
    },
    {
      title: '更新时间',
      dataIndex: 'modifyTime',
      key: 'modifyTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      fixed: 'right',
    },
  ];

  const importColumns = [
    { title: '菜品ID', dataIndex: 'foodId', key: 'foodId' },
    { title: '库存数量', dataIndex: 'stockQuantity', key: 'stockQuantity' },
  ];

  const router = useRouter();
  const loading = ref(false);
  const exporting = ref(false);
  const stockSubmitting = ref(false);
  const stockModalVisible = ref(false);
  const importModalVisible = ref(false);
  const importSubmitting = ref(false);
  const importFileInput = ref<HTMLInputElement | null>(null);
  const importFileName = ref('');
  const importRows = ref<FoodStockImportItemModel[]>([]);
  const importErrors = ref<string[]>([]);
  const currentStockFood = ref<FoodStockItemModel | null>(null);
  const shopList = ref<ShopOption[]>([]);
  const categoryList = ref<CategoryOption[]>([]);
  const searchCategoryOptions = ref<CategoryOption[]>([]);
  const tableData = ref<FoodStockItemModel[]>([]);
  const total = ref(0);
  const dateRange = ref<[Dayjs, Dayjs] | null>(null);
  const SELECTED_SHOP_KEY = 'table_manage_selected_shop';

  const stockForm = reactive({
    stockQuantity: undefined as number | undefined,
  });

  const queryParams = reactive({
    createtimeBegin: '',
    createtimeEnd: '',
    pageNum: 1,
    pageSize: 20,
    data: {
      shopId: undefined,
      shopFoodCategoryId: undefined,
      shopFoodName: '',
      shopFoodStatus: undefined,
      searchField: '',
    } as QueryData,
  });

  const resetImportState = () => {
    importFileName.value = '';
    importRows.value = [];
    importErrors.value = [];
    if (importFileInput.value) importFileInput.value.value = '';
  };

  const openImportFilePicker = () => {
    importFileInput.value?.click();
  };

  const validateImportRows = (
    rows: ImportExcelRow[],
    headers: string[],
  ): FoodStockImportItemModel[] => {
    const errors: string[] = [];
    const result: FoodStockImportItemModel[] = [];
    const foodIds = new Set<number>();

    if (!headers.includes('菜品ID')) errors.push('缺少必需列“菜品ID”');
    if (!headers.includes('库存数量')) errors.push('缺少必需列“库存数量”');
    if (errors.length > 0) {
      importErrors.value = errors;
      return result;
    }

    rows.forEach((row, index) => {
      const excelRow = index + 2;
      const rawFoodId = row['菜品ID'];
      const rawStockQuantity = row['库存数量'];
      const isBlank =
        (rawFoodId === undefined || rawFoodId === null || rawFoodId === '') &&
        (rawStockQuantity === undefined || rawStockQuantity === null || rawStockQuantity === '');
      if (isBlank) return;

      const foodId = Number(rawFoodId);
      const stockQuantity = Number(rawStockQuantity);
      let valid = true;
      if (!Number.isInteger(foodId) || foodId <= 0) {
        errors.push(`第 ${excelRow} 行：菜品ID必须为正整数`);
        valid = false;
      } else if (foodIds.has(foodId)) {
        errors.push(`第 ${excelRow} 行：菜品ID ${foodId} 重复`);
        valid = false;
      }
      if (!Number.isInteger(stockQuantity) || stockQuantity < 0) {
        errors.push(`第 ${excelRow} 行：库存数量必须为非负整数`);
        valid = false;
      }
      if (valid) {
        foodIds.add(foodId);
        result.push({ foodId, stockQuantity });
      }
    });

    if (rows.length === 0 || (result.length === 0 && errors.length === 0)) {
      errors.push('Excel 中没有可导入的数据');
    }
    importErrors.value = errors;
    return result;
  };

  const handleImportFileChange = async (event: Event) => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    resetImportState();
    importFileName.value = file.name;
    importModalVisible.value = true;
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        importErrors.value = ['Excel 中没有工作表'];
        return;
      }
      const sheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json<ImportExcelRow>(sheet, { defval: '' });
      const headerRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, range: 0 });
      const headers = (headerRows[0] || []).map((header) => String(header));
      importRows.value = validateImportRows(rawRows, headers);
    } catch (error) {
      console.error('解析库存 Excel 失败:', error);
      importErrors.value = ['文件无法解析，请确认文件是有效的 Excel 格式'];
    }
  };

  const handleImportCancel = () => {
    importModalVisible.value = false;
    resetImportState();
  };

  const handleImportConfirm = async () => {
    if (importErrors.value.length > 0 || importRows.value.length === 0) return;
    try {
      importSubmitting.value = true;
      const response = await initializeFoodStock({ stockList: importRows.value });
      if (response.code !== 200) {
        message.error(response.msg || '库存导入失败');
        return;
      }
      message.success(`成功导入 ${importRows.value.length} 条库存数据`);
      importModalVisible.value = false;
      resetImportState();
      await loadFoodList();
    } catch (error) {
      console.error('库存导入失败:', error);
      message.error('库存导入失败，请稍后重试');
    } finally {
      importSubmitting.value = false;
    }
  };

  const saveSelectedShop = (shopId: number | undefined) => {
    try {
      if (shopId) {
        localStorage.setItem(SELECTED_SHOP_KEY, String(shopId));
      } else {
        localStorage.removeItem(SELECTED_SHOP_KEY);
      }
    } catch (error) {
      console.warn('保存选中店铺失败:', error);
    }
  };

  const loadSelectedShop = () => {
    try {
      const savedShopId = localStorage.getItem(SELECTED_SHOP_KEY);
      return savedShopId ? Number(savedShopId) : undefined;
    } catch (error) {
      console.warn('读取选中店铺失败:', error);
      return undefined;
    }
  };

  const applySavedShopToQuery = async () => {
    const savedShopId = loadSelectedShop();
    const selected =
      shopList.value.find((shop) => Number(shop.shopId) === savedShopId) || shopList.value[0];
    queryParams.data.shopId = selected ? Number(selected.shopId) : undefined;
    if (!queryParams.data.shopId) return;
    saveSelectedShop(queryParams.data.shopId);
    queryParams.data.shopFoodCategoryId = undefined;
    await loadCategoryListByShop(queryParams.data.shopId);
  };

  const formatDateTime = (dateStr: string | Date | undefined) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss');
  };

  const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates[0] && dates[1]) {
      queryParams.createtimeBegin = dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
      queryParams.createtimeEnd = dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
    } else {
      queryParams.createtimeBegin = '';
      queryParams.createtimeEnd = '';
    }
  };

  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 999,
        data: { shopStatus: 0 },
      });
      if (response.code === 200) {
        shopList.value = (response.result.list || []) as ShopOption[];
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
      message.error('加载店铺列表失败');
    }
  };

  const loadCategoryList = async () => {
    try {
      const response = await fetchCategoryList({
        pageNum: 1,
        pageSize: 999,
        data: { categoryStatus: 0 },
      });
      if (response.code === 200) {
        categoryList.value = (response.result.list || []) as CategoryOption[];
      }
    } catch (error) {
      console.error('加载分类列表失败:', error);
    }
  };

  const loadCategoryListByShop = async (shopId: number) => {
    try {
      const response = await fetchCategoryList({
        pageNum: 1,
        pageSize: 999,
        data: { categoryStatus: 0, shopId },
      });
      if (response.code === 200) {
        searchCategoryOptions.value = (response.result.list || []) as CategoryOption[];
      } else {
        searchCategoryOptions.value = [];
      }
    } catch (error) {
      console.error('按店铺加载分类失败:', error);
      searchCategoryOptions.value = [];
    }
  };

  const loadFoodList = async () => {
    if (!queryParams.data.shopId) {
      tableData.value = [];
      total.value = 0;
      message.warning('请选择餐厅');
      return;
    }
    try {
      loading.value = true;
      const response = await fetchCategoryItemList(
        buildFoodQueryRequest(queryParams.pageNum, queryParams.pageSize),
      );
      if (response.code === 200) {
        tableData.value = (response.result.list || []) as FoodStockItemModel[];
        total.value = response.result.total || 0;
      } else {
        message.error(response.msg || '查询失败');
        tableData.value = [];
        total.value = 0;
      }
    } catch (error) {
      console.error('加载菜品库存列表失败:', error);
      message.error('加载菜品库存列表失败，请稍后重试');
      tableData.value = [];
      total.value = 0;
    } finally {
      loading.value = false;
    }
  };

  const buildFoodQueryRequest = (pageNum: number, pageSize: number): FoodQueryRequest => {
    const params: FoodQueryRequest = {
      pageNum,
      pageSize,
      data: {} as Partial<QueryData>,
      createtimeBegin: queryParams.createtimeBegin || undefined,
      createtimeEnd: queryParams.createtimeEnd || undefined,
    };

    if (queryParams.data.shopId !== undefined && queryParams.data.shopId !== null) {
      params.data.shopId = queryParams.data.shopId;
    }
    if (
      queryParams.data.shopFoodCategoryId !== undefined &&
      queryParams.data.shopFoodCategoryId !== null
    ) {
      params.data.shopFoodCategoryId = queryParams.data.shopFoodCategoryId;
    }
    if (queryParams.data.shopFoodName) {
      params.data.shopFoodName = queryParams.data.shopFoodName;
    }
    if (queryParams.data.shopFoodStatus !== undefined && queryParams.data.shopFoodStatus !== null) {
      params.data.shopFoodStatus = queryParams.data.shopFoodStatus;
    }
    if (queryParams.data.searchField) {
      params.data.searchField = queryParams.data.searchField;
    }

    return params;
  };

  const handleExport = async () => {
    try {
      exporting.value = true;
      const response = await fetchCategoryItemList(buildFoodQueryRequest(1, 1000));
      if (response.code !== 200) {
        message.error(response.msg || '导出数据查询失败');
        return;
      }
      const rows = (response.result.list || []) as FoodStockItemModel[];
      if (rows.length === 0) {
        message.warning('当前筛选条件下没有可导出的数据');
        return;
      }
      const exportRows = rows.map((food) => ({
        主菜品ID: food.foodId ?? '',
        店铺菜品ID: food.shopFoodId ?? '',
        菜品名称: food.shopFoodName || food.foodName || '',
        所属店铺: getShopName(food.shopId),
        所属分类: getCategoryName(food.shopFoodCategoryId),
        当前库存: food.stockQuantity ?? 0,
        状态: food.shopFoodStatus === 0 ? '上架' : '下架',
        更新时间: formatDateTime(food.modifyTime || food.createTime),
      }));
      const worksheet = XLSX.utils.json_to_sheet(exportRows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '菜品库存');
      XLSX.writeFile(workbook, `菜品库存_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      message.success(`成功导出 ${rows.length} 条库存数据`);
    } catch (error) {
      console.error('导出菜品库存失败:', error);
      message.error('导出菜品库存失败，请稍后重试');
    } finally {
      exporting.value = false;
    }
  };

  const handleSearch = () => {
    queryParams.pageNum = 1;
    loadFoodList();
  };

  const handleReset = async () => {
    queryParams.createtimeBegin = '';
    queryParams.createtimeEnd = '';
    queryParams.pageNum = 1;
    queryParams.pageSize = 20;
    queryParams.data.shopFoodCategoryId = undefined;
    queryParams.data.shopFoodName = '';
    queryParams.data.shopFoodStatus = undefined;
    queryParams.data.searchField = '';
    dateRange.value = null;
    await loadFoodList();
  };

  const handleShopChange = async (value: number | undefined) => {
    queryParams.data.shopId = value;
    queryParams.data.shopFoodCategoryId = undefined;
    saveSelectedShop(value);
    if (!value) {
      searchCategoryOptions.value = [];
      return;
    }
    await loadCategoryListByShop(Number(value));
  };

  const handlePageChange = (page: number, pageSize: number) => {
    queryParams.pageNum = page;
    queryParams.pageSize = pageSize;
    loadFoodList();
  };

  const handlePageSizeChange = (current: number, size: number) => {
    queryParams.pageNum = current;
    queryParams.pageSize = size;
    loadFoodList();
  };

  const getShopName = (shopId?: number) => {
    const shop = shopList.value.find((item) => item.shopId === shopId);
    return shop?.shopName || '-';
  };

  const getCategoryName = (categoryId?: number) => {
    const category = categoryList.value.find((item) => item.shopFoodCategoryId === categoryId);
    return category?.categoryName || '-';
  };

  const getFoodTagNames = (tags: unknown): string[] => {
    if (typeof tags !== 'string') return [];
    return tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag);
  };

  const getStockColor = (stockQuantity?: number) => {
    const stock = stockQuantity ?? 0;
    if (stock === 0) return 'red';
    if (stock <= 10) return 'orange';
    return 'green';
  };

  const openStockModal = async (food: FoodStockItemModel) => {
    if (!food.foodId) {
      message.warning('缺少主菜品ID');
      return;
    }
    currentStockFood.value = food;
    stockForm.stockQuantity = food.stockQuantity ?? 0;
    stockModalVisible.value = true;
    await nextTick();
    const input = document.querySelector<HTMLInputElement>('.stock-form input');
    input?.focus();
    input?.select();
  };

  const handleStockCancel = () => {
    stockModalVisible.value = false;
    stockSubmitting.value = false;
    currentStockFood.value = null;
    stockForm.stockQuantity = undefined;
  };

  const handleStockConfirm = async () => {
    if (!currentStockFood.value?.foodId) {
      message.warning('请先选择菜品');
      return;
    }
    if (stockForm.stockQuantity === undefined || stockForm.stockQuantity === null) {
      message.warning('请输入库存数量');
      return;
    }
    if (!Number.isInteger(stockForm.stockQuantity) || stockForm.stockQuantity < 0) {
      message.warning('库存数量必须为非负整数');
      return;
    }

    stockSubmitting.value = true;
    try {
      const response = await initializeFoodStock({
        stockList: [
          {
            foodId: currentStockFood.value.foodId,
            stockQuantity: stockForm.stockQuantity,
          },
        ],
      });
      if (response.code === 200) {
        message.success('库存修改成功');
        handleStockCancel();
        await loadFoodList();
      } else {
        message.error(response.msg || '库存修改失败');
      }
    } catch (error) {
      console.error('修改菜品库存异常:', error);
      message.error('修改菜品库存异常，请稍后重试');
    } finally {
      stockSubmitting.value = false;
    }
  };

  const goHome = () => {
    router.back();
  };

  onMounted(async () => {
    await loadShopList();
    await applySavedShopToQuery();
    await loadCategoryList();
    await loadFoodList();
  });
</script>

<style scoped lang="less">
  .food-stock-manage {
    padding: 0;
    background: #f0f2f5;
    min-height: 100vh;

    .page-header {
      min-height: 62px;
      margin: 0 0 16px;
      padding: 10px 16px;
      background: #3b82f6;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    .page-back-btn {
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 8px 12px;
      margin-right: 16px;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .page-back-btn:hover {
      background: #1d4ed8;
    }

    .page-title {
      color: #fff;
      font-size: 24px;
      font-weight: 600;
    }

    .search-form {
      margin-bottom: 16px;
      padding: 16px;
      background: #fff;
      border-radius: 4px;

      :deep(.ant-form-item) {
        margin-bottom: 16px;
      }
    }

    .pagination-container {
      margin-top: 16px;
      text-align: right;
    }

    :deep(.ant-card) {
      border-radius: 0;
      box-shadow: none;
      margin: 0;
      width: 100%;

      .ant-card-head {
        border-bottom: 1px solid #f0f0f0;
        display: none;
      }

      .ant-card-body {
        padding: 0;
      }
    }

    :deep(.ant-table) {
      .ant-table-thead > tr > th {
        background: #fafafa;
        font-weight: 600;
      }
    }
  }

  .stock-form {
    margin-top: 16px;
  }
</style>
